import moment from 'moment';
import { ADDRESS_1_MAX_LENGTH, ADDRESS_2_MAX_LENGTH, ANSWER_MAX_LENGTH, BONUS_CODE_MAX_LENGTH, CITY_MAX_LENGTH, CITY_MIN_LENGTH, FIRST_NAME_MAX_LENGTH, LAST_NAME_MAX_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, POST_CODE_MAX_LENGTH, POST_CODE_MIN_LENGTH, USERNAME_MAX_LENGTH, USERNAME_MIN_LENGTH } from '../../constants/signUp';
import PHONE_PREFIX_DATA, { PHONE_PREFIX_COUNTRY_CANADA } from '../../data/phonePrefix';

const errorResponse = (field, text, data) => {
  if (data) {
    return { error: text, data };
  }
  return text;
};

/* eslint-disable no-useless-escape */
const hasForbiddenChars = (value) => (
  /\W|_/g.test(value.split(' ').join(''))
);

const hasNumericChars = value => (
  /\d/.test(value)
);

function validateUsername(username, password) {
  const url = `/bpapi/rest/account/username/${username}`;
  const GET_OPTIONS = {
    credentials: 'same-origin',
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  };

  if (!/^[A-Za-z0-9\_\-]*$/.test(username)) {
    return Promise.reject(errorResponse('username', 'Please only use letters, numbers and dashes'));
  }

  if (username.length > USERNAME_MAX_LENGTH) {
    return Promise.reject(errorResponse('username', `Must be between ${USERNAME_MIN_LENGTH} and ${USERNAME_MAX_LENGTH} characters long`));
  }

  if (username.length < USERNAME_MIN_LENGTH) {
    return Promise.reject(errorResponse('username', `Must be between ${USERNAME_MIN_LENGTH} and ${USERNAME_MAX_LENGTH} characters long`));
  }

  if (password !== '' && username.indexOf(password) > -1) {
    return Promise.reject(errorResponse('username', 'Username and Password are too similar.'));
  }

  if (username.length >= USERNAME_MIN_LENGTH && username.length <= USERNAME_MAX_LENGTH) {
    return new Promise((resolve, reject) => {
      fetch(url, GET_OPTIONS)
        .then(response => response.json())
        .then((json) => {
          if (json.available) {
            resolve(json);
          } else if (json.errors.length > 0) {
            reject(errorResponse('username', json.errors[0].messages[0]));
          } else {
            reject(errorResponse('username', 'Unexpected username Error.'));
          }
        })
        .catch(() => {
          reject(errorResponse('username', 'Unexpected username Error.'));
        });
    });
  }

  return Promise.reject(errorResponse('username', 'Unexpected username Error.'));
}

function validatePassword(password, username = '') {
  if (!/^\s*$/.test(password)) {
    if (password.length < PASSWORD_MIN_LENGTH) {
      return errorResponse('password', `Must be at least ${PASSWORD_MIN_LENGTH} characters long`);
    }

    if (password.length > PASSWORD_MAX_LENGTH) {
      return errorResponse('password', `Must be less than ${PASSWORD_MAX_LENGTH} characters long`);
    }

    // eslint-disable-next-line no-control-regex
    if (!/^[\x00-\xFF]{8,64}$/.test(password)) {
      return errorResponse('password', 'Please only use letters, numbers and symbols.');
    }

    // this excludes the letters and numbers
    // eslint-disable-next-line no-control-regex
    const hasSymbols = /[\x00-\x2F\x3a-\x40\x5b-\x60\x7b-\xff]+/.test(password);

    const hasNumber = /[0-9]+/.test(password);

    if (!hasSymbols && !hasNumber) {
      return errorResponse('password', 'Password must contain at least one symbol and one number.');
    }

    if (!hasSymbols) {
      return errorResponse('password', 'Password must contain at least one symbol.');
    }

    if (!hasNumber) {
      return errorResponse('password', 'Password must contain at least one number.');
    }

    if (username !== '' && password.indexOf(username) > -1) {
      return errorResponse('password', 'Password and Username are too similar.');
    }

    return false;
  }

  return errorResponse('password', 'Password cannot be empty');
}

function validatePasswordConfirmation(password, confirmationPassword) {
  if (confirmationPassword !== password) {
    return errorResponse('password-confirmation', 'Passwords do not match.');
  }
  return false;
}

function validateEmail(email) {
  const url = `/bpapi/rest/account/email/${email}`;
  const GET_OPTIONS = {
    credentials: 'same-origin',
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  };

  if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(email)) {
    return Promise.reject(errorResponse('email', 'Please enter a valid email address.'));
  }

  return new Promise((resolve, reject) => {
    fetch(url, GET_OPTIONS)
      .then(response => response.json())
      .then((json) => {
        if (json.available) {
          resolve(json);
        } else if (json.errors.length > 0) {
          const redirectUrl = json.errors[0]['redirect-url'];
          if (redirectUrl) {
            reject(errorResponse('email', 'This email address is not available on easyBet', { redirectUrl }));
          }
          reject(errorResponse('email', json.errors[0].messages[0]));
        } else {
          reject(errorResponse('email', 'Unexpected email Error.'));
        }
      });
  });
}

function validateBonusCode(bonusCode) {
  if (/^[a-zA-Z0-9]*$/.test(bonusCode)
    && bonusCode.length <= BONUS_CODE_MAX_LENGTH) {
    return false;
  }
  return errorResponse('bonus-code', 'This bonus code is not recognised');
}

function validatePhoneNumber(phoneNumber = '', { abslength, withSpecialChars = true } = {}) {
  const phoneNumberToValidate = phoneNumber ? phoneNumber.replace(/ /g, '').trim() : '';
  const regex = withSpecialChars ? /^[0-9\-\+\(\)\s]*$/ : /^[0-9(\)\s]*$/;
  if (!abslength) {
    return false;
  }

  const lengthInvalid = withSpecialChars
    ? phoneNumberToValidate.length < abslength || phoneNumberToValidate.length > abslength + 1
    : phoneNumberToValidate.length !== abslength;

  if (Array.isArray(abslength)) {
    if (regex.test(phoneNumberToValidate)
      && phoneNumberToValidate.length > abslength[1] + 1
      || phoneNumberToValidate.length < abslength[0]) {
      return errorResponse('phone-number', `Must be between ${abslength[0]} and ${abslength[1] + 1} digits long`);
    }
    if (regex.test(phoneNumberToValidate)
      && phoneNumberToValidate.length <= abslength[1] + 1
      && phoneNumberToValidate.length >= abslength[0]) {
      return false;
    }
    if (phoneNumberToValidate === 0) return false;
  } else {
    if (!regex.test(phoneNumberToValidate)) {
      return errorResponse('phone-number', 'Please enter a valid phone number');
    }

    if (regex.test(phoneNumberToValidate)) {
      if (lengthInvalid && phoneNumberToValidate.length > 0) {
        if (withSpecialChars) {
          return errorResponse('phone-number', `Must be between ${abslength} and ${abslength + 1} digits long`);
        }

        return errorResponse('phone-number', `Must be ${abslength} digits long`);
      }
    }

    if (regex.test(phoneNumberToValidate)
      && phoneNumberToValidate.length <= abslength + 1
      && phoneNumberToValidate.length >= abslength) {
      return false;
    }
  }
  return errorResponse('phone-number', 'Please enter a valid mobile number.');
}

function validateFirstName(firstName) {
  if (firstName.length > FIRST_NAME_MAX_LENGTH) {
    return errorResponse('first-name', `Must be less than ${FIRST_NAME_MAX_LENGTH} characters long`);
  }
  if (hasForbiddenChars(firstName) || hasNumericChars(firstName)) {
    return errorResponse('first-name', 'Only letters allowed');
  }
  return false;
}

function validateLastName(lastName) {
  if (lastName.length > LAST_NAME_MAX_LENGTH) {
    return errorResponse('last-name', `Must be less than ${LAST_NAME_MAX_LENGTH} characters long`);
  }
  if (hasForbiddenChars(lastName) || hasNumericChars(lastName)) {
    return errorResponse('last-name', 'Only letters allowed');
  }
  return false;
}

function validatePostCode(postCode) {
  if (!/^[0-9a-zA-Z\-\+\(\)\s]*$/.test(postCode)) {
    return errorResponse('post-code', 'Please enter a valid post code');
  }
  if (/^[0-9a-zA-Z\-\+\(\)\s]*$/.test(postCode) && postCode.length > POST_CODE_MAX_LENGTH) {
    return errorResponse('post-code', `Must be less than ${POST_CODE_MAX_LENGTH} characters long`);
  }
  if (/^[0-9a-zA-Z\-\+\(\)\s]*$/.test(postCode) && postCode.length < POST_CODE_MIN_LENGTH) {
    return errorResponse('post-code', `Must be at least ${POST_CODE_MIN_LENGTH} characters long`);
  }
  if (/^[0-9a-zA-Z\-\+\(\)\s]*$/.test(postCode)
    && postCode.length <= POST_CODE_MAX_LENGTH && postCode.length >= POST_CODE_MIN_LENGTH
  ) {
    return false;
  }
  return errorResponse('post-code', 'Unexpected post code Error.');
}

function validateCity(city) {
  if (!/^[^\<\>\!`\^\=\+\|\_\~\|]*$/.test(city)) {
    return errorResponse('city', 'The following characters are not allowed: < > ! ` ^ = + _ ~ |');
  }

  if (/^[^\<\>\!`\^\=\+\|\_\~\|]*$/.test(city) && city.length > CITY_MAX_LENGTH) {
    return errorResponse('city', `Must be less than ${CITY_MAX_LENGTH} characters long`);
  }

  if (/^[^\<\>\!`\^\=\+\|\_\~\|]*$/.test(city) && city.length < CITY_MIN_LENGTH) {
    return errorResponse('city', `Must be at least ${CITY_MIN_LENGTH} characters long`);
  }

  if (/^[^\<\>\!`\^\=\+\|\_\~\|]*$/.test(city) && city.length <= CITY_MAX_LENGTH) {
    return false;
  }

  return errorResponse('city', 'Unexpected city Error.');
}

function validateAnswer(answer) {
  if (!/^[^\<\>\!`\^\=\+\|\_\~\|]*$/.test(answer)) {
    return errorResponse('answer', 'Please only use letters or numbers');
  }

  if (/^[^\<\>\!`\^\=\+\|\_\~\|]*$/.test(answer)) {
    if (answer.length > ANSWER_MAX_LENGTH) {
      return errorResponse('answer', `Must be less than ${ANSWER_MAX_LENGTH} characters long`);
    }
    return false;
  }

  return errorResponse('answer', 'Unexpected answer Error.');
}

function validateDOB(dateOfBirth = '') {
  const momentDate = moment(dateOfBirth, 'DD/MM/YYYY');

  if (!momentDate.isValid() || momentDate.isBefore(moment().subtract(110, 'y'))) {
    return errorResponse('date-of-birth', 'Not a valid date of birth.');
  }

  if (momentDate.isValid()) {
    if (momentDate.isBefore(moment().subtract(18, 'y'))) {
      return false;
    }
    if (momentDate.isAfter(moment().subtract(18, 'y'))) {
      return errorResponse('date-of-birth', 'You have to be over 18 years old to register.');
    }
  } else {
    return errorResponse('date-of-birth', 'Not a valid date of birth.');
  }

  return errorResponse('date-of-birth', 'Unexpected DOB Error');
}

function validateUserType(type) {
  if (type === '') {
    return errorResponse('user-type', 'Please select an option.');
  }

  return false;
}

function validateAmount(amount, maxLimit, minLimit) {
  if (!/^\s*(?=.*[0-9])\d*(?:\.\d{1,2})?\s*$/.test(amount)) {
    return errorResponse('amount', 'Please enter a valid number.');
  }

  if (/^\s*(?=.*[0-9])\d*(?:\.\d{1,2})?\s*$/.test(amount)
    && maxLimit
    && amount > maxLimit) {
    return errorResponse('amount', `Value cannot exceed ${maxLimit}`);
  }

  if (/^\s*(?=.*[0-9])\d*(?:\.\d{1,2})?\s*$/.test(amount)
    && minLimit
    && amount < minLimit) {
    return errorResponse('amount', `Value cannot be under ${minLimit}`);
  }

  return false;
}

function validateAddress1(address) {
  if (!/^[^\<\>\!`\^\=\+\|\_\~\|]*$/.test(address)) {
    return errorResponse('address-line-1', 'The following characters are not allowed: < > ! ` ^ = + _ ~ |');
  }

  if (/^[^\<\>\!`\^\=\+\|\_\~\|]*$/.test(address) && address.length > ADDRESS_1_MAX_LENGTH) {
    return errorResponse('address-line-1', `Must be less than ${ADDRESS_1_MAX_LENGTH} characters long`);
  }
  if (/^[^\<\>\!`\^\=\+\|\_\~\|]*$/.test(address) && address.length <= ADDRESS_1_MAX_LENGTH) {
    return false;
  }

  return errorResponse('address-line-1', 'Unexpected address-line-1 Error.');
}

function validateAddress2(address) {
  if (!/^[^\<\>\!`\^\=\+\|\_\~\|]*$/.test(address)) {
    return errorResponse('address-line-2', 'The following characters are not allowed: < > ! ` ^ = + _ ~ |');
  }

  if (/^[^\<\>\!`\^\=\+\|\_\~\|]*$/.test(address) && address.length > ADDRESS_2_MAX_LENGTH) {
    return errorResponse('address-line-2', `Must be less than ${ADDRESS_2_MAX_LENGTH} characters long`);
  }
  if (/^[^\<\>\!`\^\=\+\|\_\~\|]*$/.test(address) && address.length <= ADDRESS_2_MAX_LENGTH) {
    return false;
  }
  return errorResponse('address-line-2', 'Unexpected address-line-1 Error.');
}

/* eslint-enable no-useless-escape */

const validationMap = {
  username: validateUsername,
  email: validateEmail,
  password: validatePassword,
  'password-confirmation': validatePasswordConfirmation,
  'bonus-code': validateBonusCode,
  'phone-number': validatePhoneNumber,
  first: validateFirstName,
  last: validateLastName,
  'post-code': validatePostCode,
  city: validateCity,
  answer: validateAnswer,
  'date-of-birth': validateDOB,
  'user-type': validateUserType,
  amount: validateAmount,
  'address-line-1': validateAddress1,
  'address-line-2': validateAddress2
};

export const phoneValidationWithSpecialChars = (prefix) => {
  const [, phoneCountryCode] = PHONE_PREFIX_DATA.find(data => data[2] === prefix) || [];
  const validateWithSpecialChars = phoneCountryCode !== PHONE_PREFIX_COUNTRY_CANADA;

  return validateWithSpecialChars;
}

export default function validate(key, ...args) {
  if (validationMap[key]) {
    return validationMap[key](...args);
  }
  return false;
}
